/* eslint-disable complexity */
import React, {
    AnchorHTMLAttributes,
    ButtonHTMLAttributes,
    HTMLAttributes,
    memo,
    MouseEventHandler,
    ReactNode,
    useCallback,
    useMemo,
} from 'react';
import Link from 'components/CustomRoutingLink';
import {PREVENT_DEFAULT_HANDLER_MARKER, STOP_PROP_HANDLER_MARKER} from 'core/constants';
import classNames from 'classnames';
import style from './style.module.scss';
import {TTypographyVariant, Typography} from 'components/Typography';
import {Loader} from 'components/Loader';
import {LinkProps} from 'next/link';

/*
     isSquare - квадратная кнопка
     isNotAdaptive - не уменьшается в мобильной версии
     isStretched - растягивается до ширины родителя
     isWithoutPaddings - обнуляем падинги
     isWithoutTextLoading - убираем текст во время загрузки (только лоадер)
     isContentSize - размеры кнопки (ширина и высота) под размер контента (особое состояние для Ghost)
 */

export const BUTTON_THEMES = {
    ghost: 'ghost',
    gray: 'gray',
    outline: 'outline',
    primary: 'primary',
    secondary: 'secondary',
    unset: 'unset',
} as const;

export type TButtonTheme = keyof typeof BUTTON_THEMES;

export const BUTTON_SIZE = {
    large: 'large',
    medium: 'medium',
    small: 'small',
    huge: 'huge',
    unset: 'unset',
} as const;

export type TButtonSize = keyof typeof BUTTON_SIZE;

export const BUTTON_COLOR = {
    gray: 'gray',
    red: 'red',
    white: 'white',
};

export type TButtonColor = keyof typeof BUTTON_COLOR;

export interface IProps
    extends
        ButtonHTMLAttributes<HTMLButtonElement | HTMLAnchorElement>,
        Pick<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'target' | 'rel'> {
    className?: string;
    theme?: TButtonTheme;
    backColor?: TButtonColor;
    size?: TButtonSize;
    isLoading?: boolean;
    disabled?: boolean;
    children?: ReactNode;

    // флаги состояния
    isSquare?: boolean;
    isNotAdaptive?: boolean;
    isStretched?: boolean;
    isWithoutPaddings?: boolean;
    isWithoutTextLoading?: boolean;
    isContentSize?: boolean;

    // иконки
    leftIcon?: ReactNode;
    rightIcon?: ReactNode;

    // стилизация
    textClassName?: string;

    // маркеры
    [PREVENT_DEFAULT_HANDLER_MARKER]?: boolean;
    [STOP_PROP_HANDLER_MARKER]?: boolean;
}

// типография для классической кнопки
const useTypographyVariant = (size: TButtonSize): TTypographyVariant => {
    return useMemo(() => {
        const sizeMap: Record<TButtonSize, TTypographyVariant> = {
            large: 'button-l',
            medium: 'button-12-m-cl',
            small: 'button-s',
            huge: 'button-12-m-cl',
            unset: 'button-12-m-cl',
        };
        return sizeMap[size] || 'button-12-m-cl';
    }, [size]);
};

// контент
const ButtonContent = memo<{
    isLoading: boolean;
    children: ReactNode;
    leftIcon: ReactNode;
    rightIcon: ReactNode;
    isWithoutTextLoading: boolean;
    typographyVariant: TTypographyVariant;
    textClassName?: string;
}>(function ButtonContent({
    isLoading,
    children,
    leftIcon,
    rightIcon,
    isWithoutTextLoading,
    typographyVariant,
    textClassName,
}) {
    return (
        <>
            {isLoading && leftIcon ? <Loader /> : leftIcon}

            {!isLoading && children && (
                <Typography
                    className={classNames(style.text, textClassName, {
                        [style.isNotDisplayed]: isWithoutTextLoading && isLoading,
                    })}
                    variant={typographyVariant}
                >
                    {children}
                </Typography>
            )}

            {isLoading && !leftIcon && !rightIcon && <Loader />}
            {isLoading && rightIcon ? <Loader /> : rightIcon}
        </>
    );
});

export const Button = memo<IProps>(function Button({
    theme = BUTTON_THEMES.primary,
    backColor = '',
    isLoading = false,
    disabled = false,
    size = BUTTON_SIZE.large,
    className,
    children,
    isSquare = false,
    isStretched = false,
    isWithoutPaddings = false,
    isContentSize = false,
    isWithoutTextLoading = false,
    leftIcon,
    rightIcon,
    type = 'button',
    onClick,
    href,
    target,
    rel,
    textClassName,
    isNotAdaptive,
    ...restButtonAttributes
}) {
    const typographyVariant = useTypographyVariant(size);

    const handleClick: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement> = useCallback(
        (e) => {
            if (isLoading) {
                e.preventDefault();
            } else {
                onClick?.(e);
            }
        },
        [isLoading, onClick],
    );

    const TargetElement = useMemo<typeof Link | 'button'>(() => {
        return href ? Link : 'button';
    }, [href]);

    const targetAttributes = useMemo<HTMLAttributes<HTMLButtonElement | HTMLAnchorElement | {href: string}>>(() => {
        if (Link === TargetElement) {
            return {
                href,
                rel,
                target,
            } as HTMLAttributes<HTMLAnchorElement> & LinkProps;
        }

        return {
            type,
        } as HTMLAttributes<HTMLButtonElement>;
    }, [TargetElement, href, rel, target, type]);

    const isOnlyIcon = useMemo(
        () => isLoading || (!children && (leftIcon || rightIcon)),
        [children, isLoading, leftIcon, rightIcon],
    );

    const buttonClasses = useMemo(
        () =>
            classNames(className, style.button, 'simple-button', {
                [style.isContentSize]: isContentSize,
                [style.isLoading]: isLoading,
                [style.isSquare]: isSquare,
                [style.isStretched]: isStretched,
                [style.isWithoutPaddings]: isWithoutPaddings,
                [style[theme]]: Boolean(theme),
                [style[size]]: Boolean(size),
                [style[backColor]]: Boolean(backColor),
                [style.isNotAdaptive]: isNotAdaptive,
                ['button-only-icon']: isOnlyIcon,
            }),
        [
            className,
            isContentSize,
            isLoading,
            isSquare,
            isStretched,
            isWithoutPaddings,
            theme,
            size,
            backColor,
            isNotAdaptive,
            isOnlyIcon,
        ],
    );

    return (
        <TargetElement
            href={href || ''}
            {...targetAttributes}
            {...restButtonAttributes}
            className={buttonClasses}
            disabled={disabled}
            onClick={handleClick}
        >
            <ButtonContent
                isLoading={isLoading}
                children={children}
                leftIcon={leftIcon}
                rightIcon={rightIcon}
                isWithoutTextLoading={isWithoutTextLoading}
                typographyVariant={typographyVariant}
                textClassName={textClassName}
            />
        </TargetElement>
    );
});
